import { useCallback, useState } from 'react'

import { useController, useFormContext } from 'react-hook-form'

import { useOutsideClickDetector } from '@/modules/tiktok-research/hooks/useOutsideClickDetector.tsx'

import { IconPicker } from './icon-picker/IconPicker.tsx'
import type { IconName } from './icon-picker/consts'
import { iconsMap } from './icon-picker/consts'

interface Props {
  name: string
  label?: string
  placeholder?: string
  color: string
}

interface FormValues {
  [K: string]: IconName
}

export const FormPickIcon = ({ name, label, placeholder, color }: Props) => {
  const { control } = useFormContext<FormValues>()
  const { field } = useController({ name, control })
  const [isIconPickerVisible, setIsIconPickerVisible] = useState(false)

  const [iconPickerRef, setIconPickerRef] = useState<HTMLDivElement | null>(
    null,
  )

  const handleOpenIconPicker = useCallback(() => {
    setIsIconPickerVisible(true)
  }, [])

  const handleCloseIconPicker = useCallback(() => {
    setIsIconPickerVisible(false)
  }, [])

  useOutsideClickDetector(iconPickerRef, handleCloseIconPicker)

  const Icon = iconsMap[field.value]

  return (
    <div className="flex flex-1 flex-col gap-2">
      {label && (
        <label
          htmlFor={field.name}
          className="text-text-default text-sm font-medium"
        >
          {label}
        </label>
      )}
      <div
        onClick={handleOpenIconPicker}
        className="bg-card ring-offset-background relative flex h-10 w-full items-center justify-between gap-2 rounded-md border px-3 py-2 text-left text-sm text-gray-700 disabled:cursor-not-allowed disabled:opacity-50 dark:text-white"
      >
        {Icon && (
          <Icon
            className="h-5 w-5"
            style={{ fill: color }}
          />
        )}
        {!Icon && placeholder && (
          <span className="text-secondary-300">{placeholder}</span>
        )}
        {isIconPickerVisible && (
          <div
            className="bg-card absolute left-0 top-[calc(100%+8px)] z-50 rounded-2xl p-3 shadow-2xl"
            ref={setIconPickerRef}
          >
            <IconPicker
              icon={field.value}
              onChange={(iconName, event) => {
                event.stopPropagation()
                field.onChange(iconName)
                handleCloseIconPicker()
              }}
            />
          </div>
        )}
      </div>
    </div>
  )
}
